import React, {useState, useRef, useEffect, useCallback} from "react";
import "../emoji/style.scss";
import {Button, Tooltip} from "antd";
import {useInfoUser} from "../../../store/UserStore";
import apiFactory from "../../../api";
import {openNotificationError} from "../../../utils/Utils";
import StickerItem from "./StickerItem";
import EmojiTable from "../emoji/EmojiTable";
import {StickerIcon} from "../../../config/Icon";

export default function StickerTable({
                                           onStickerSend,
                                           onEmojiSelect,
                                       }) {
    const {languageMap} = useInfoUser();
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("sticker");
    const buttonRef = useRef(null);
    const popoverRef = useRef(null);

    const [position, setPosition] = useState({top: 0, left: 0});
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({current: 1, pageSize: 5, total: 0});
    const [stickerPacks, setStickerPacks] = useState([]);

    const observerRef = useRef(null);

    const fetchStickerPacksWithStickers = useCallback(
        async (page = 1, pageSize = 5, append = false) => {
            setLoading(true);
            try {
                const request = {
                    page: page - 1,
                    limit: pageSize
                }
                const res = await apiFactory.stickerPackApi.getStickerPacksWithStickers(request);
                if (res?.status === 200) {
                    const data = res.data.content || [];
                    setStickerPacks((prev) => (append ? [...prev, ...data] : data));
                    setPagination({
                        current: page,
                        pageSize,
                        total: res.data.totalElements,
                    });
                }
            } catch {
                openNotificationError("Failed to fetch sticker packs");
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const lastStickerPackRef = useCallback(
        (node) => {
            if (loading) return;
            if (observerRef.current) observerRef.current.disconnect();

            observerRef.current = new IntersectionObserver((entries) => {
                const first = entries[0];
                if (first.isIntersecting && stickerPacks.length < pagination.total) {
                    fetchStickerPacksWithStickers(pagination.current + 1, pagination.pageSize, true);
                }
            });

            if (node) observerRef.current.observe(node);
        },
        [loading, pagination, stickerPacks, fetchStickerPacksWithStickers]
    );

    useEffect(() => {
        fetchStickerPacksWithStickers();
    }, [fetchStickerPacksWithStickers]);

    useEffect(() => {
        if (open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.top - 450,
                left: rect.left,
            });
        }
    }, [open]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target) &&
                !buttonRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="sticker-popover-wrapper">
            <Tooltip
                placement="top"
                title={
                    languageMap?.["modal.conversation.sticker"] ??
                    "Sticker"
                }
                color={"#0091ff"}
            >
                <Button
                    ref={buttonRef}
                    className="bg-[#2a56b9] text-[white] hover:text-[#2a56b9]"
                    icon={<StickerIcon width={20} height={20} className="text-[18px]"/>}
                    onClick={() => setOpen(!open)}
                />
            </Tooltip>

            {open && (
                <div
                    ref={popoverRef}
                    className="sticker-popover"
                    style={{top: position.top, left: position.left}}
                >
                    <div className="tab-header">
                        <button
                            className={tab === "sticker" ? "active" : ""}
                            onClick={() => setTab("sticker")}
                        >
                          {languageMap?.["modal.conversation.tab.stickers"] ?? "Stickers"}
                        </button>
                        <button
                            className={tab === "emoji" ? "active" : ""}
                            onClick={() => setTab("emoji")}
                        >
                          {languageMap?.["modal.conversation.tab.emojis"] ?? "Emojis"}
                        </button>
                    </div>

                    <div className="tab-content">
                        {tab === "sticker" ? (
                            <>
                                {/* Optional search input */}
                                {/* <input type="text" placeholder="Search stickers..." className="search-input" /> */}

                                <div className="overflow-y-auto h-[100%] py-1">
                                    {stickerPacks.map((item, index) => {
                                            const isLastItem = index === stickerPacks.length - 1;

                                            return (
                                                <div
                                                    key={item.packId}
                                                    ref={isLastItem ? lastStickerPackRef : null}
                                                >
                                                    <span className="m-2 font-medium">{item.name}</span>
                                                    <div className="sticker-grid">
                                                        {
                                                            item.stickers.map(s => (
                                                                <div
                                                                    key={s.stickerId}
                                                                    className="sticker-item"
                                                                    onClick={() => {
                                                                        onStickerSend?.(s);
                                                                        setOpen(false);
                                                                    }}
                                                                >
                                                                    <StickerItem
                                                                        resourceId={s.resourceId}
                                                                        frameCount={s.frameCount}
                                                                        size={s.width / s.frameCount > 100 ? 75 : s.width / s.frameCount}
                                                                    />
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            )
                                        }
                                    )}

                                    {loading && (
                                        <div className="text-center text-gray-400 py-2">Loading...</div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <EmojiTable onSelect={(emoji) => {
                                onEmojiSelect?.(emoji);
                            }}/>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
